import {
  Box,
  Icon,
  IconButton,
  Image as ChakraImage,
  Tooltip,
  Wrap,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import {
  IoAddOutline,
  IoExpandOutline,
  IoReloadOutline,
  IoRemoveOutline,
} from "react-icons/io5";
import { useIntl } from "react-intl";

/**
 * @typedef {Object} Props
 * @property {string} blobUrl
 *
 * @param {Props} props
 */
function ImageRenderer({ blobUrl }) {
  const intl = useIntl();

  const [rotation, setRotation] = useState(0);
  const [{ translateX, translateY }, setTranslate] = useState({
    translateX: 0,
    translateY: 0,
  });

  const [scale, setScale] = useState(1);

  const handleZoomOut = useCallback(() => {
    setScale((value) => Math.max(0.1, value - 0.1));
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((value) => value + 0.1);
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((value) => {
      const newValue = value + 90;
      return newValue >= 360 ? 0 : newValue;
    });
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setRotation(0);
    setTranslate({ translateX: 0, translateY: 0 });
  }, []);

  const handleMouseDown = useCallback(
    (event) => {
      const startX = event.pageX - translateX;
      const startY = event.pageY - translateY;

      const handleMouseMove = (event) => {
        event.preventDefault();

        const newTranslateX = event.pageX - startX;
        const newTranslateY = event.pageY - startY;

        setTranslate({ translateX: newTranslateX, translateY: newTranslateY });
      };

      const handleMouseUp = (event) => {
        event.preventDefault();

        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [translateX, translateY],
  );

  const handleTouchStart = useCallback(
    (event) => {
      const startX = event.touches[0].pageX - translateX;
      const startY = event.touches[0].pageY - translateY;

      const handleTouchMove = (event) => {
        event.preventDefault();

        const newTranslateX = event.touches[0].pageX - startX;
        const newTranslateY = event.touches[0].pageY - startY;

        setTranslate({ translateX: newTranslateX, translateY: newTranslateY });
      };

      const handleTouchEnd = (event) => {
        event.preventDefault();

        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      };

      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleTouchEnd);
    },
    [translateX, translateY],
  );

  return (
    <>
      <Box
        transform={`translateX(${`${translateX}px`}) translateY(${translateY}px)`}
        h="100%">
        <ChakraImage
          src={blobUrl}
          objectFit="contain"
          transform={`scale(${scale}) rotate(${rotation}deg)`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          cursor="grab"
          alt="image"
          w="full"
          maxW="full"
          h="full"
          maxH="full"
        />
      </Box>

      <Box position="absolute" top=".5rem" left=".5rem" right=".5rem">
        <Wrap
          display="inline-flex"
          spacing=".25rem"
          opacity=".8"
          _hover={{ opacity: 1 }}
          w="auto">
          <Tooltip label={intl.formatMessage({ defaultMessage: "Pivoter" })}>
            <IconButton
              icon={<Icon as={IoReloadOutline} />}
              onClick={handleRotate}
              size="sm"
              aria-label={intl.formatMessage({
                defaultMessage: "Pivoter",
              })}
            />
          </Tooltip>

          <Tooltip label={intl.formatMessage({ defaultMessage: "Dézoomer" })}>
            <IconButton
              icon={<Icon as={IoRemoveOutline} />}
              onClick={handleZoomOut}
              size="sm"
              isDisabled={scale === 0.1}
              aria-label={intl.formatMessage({
                defaultMessage: "Dézoomer",
              })}
            />
          </Tooltip>

          <Tooltip label={intl.formatMessage({ defaultMessage: "Zoomer" })}>
            <IconButton
              icon={<Icon as={IoAddOutline} />}
              onClick={handleZoomIn}
              size="sm"
              aria-label={intl.formatMessage({
                defaultMessage: "Zoomer",
              })}
            />
          </Tooltip>

          <Tooltip label={intl.formatMessage({ defaultMessage: "Pleine vue" })}>
            <IconButton
              icon={<Icon as={IoExpandOutline} />}
              onClick={handleReset}
              size="sm"
              aria-label={intl.formatMessage({
                defaultMessage: "Pleine vue",
              })}
            />
          </Tooltip>
        </Wrap>
      </Box>
    </>
  );
}

export default ImageRenderer;
